import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";

/**
 * Reusable FormDialog component for generic form modals
 * 
 * @param {boolean} open - Dialog open state
 * @param {string} title - Dialog title
 * @param {Function} onClose - Close handler
 * @param {Function} onSubmit - Submit handler
 * @param {boolean} loading - Loading state
 * @param {string} submitLabel - Submit button label (default: "Save")
 * @param {string} cancelLabel - Cancel button label (default: "Cancel")
 * @param {boolean} disableSubmit - Disable submit button
 * @param {ReactNode} children - Form content
 * @param {number} maxWidth - Dialog max width (xs, sm, md, lg, xl)
 * @param {boolean} fullWidth - Full width dialog
 * @param {Object} sx - Custom styles
 */
const FormDialog = ({
  open,
  title,
  onClose,
  onSubmit,
  loading = false,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  disableSubmit = false,
  children,
  maxWidth = "sm",
  fullWidth = true,
  sx = {},
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.();
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...sx,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {title}
            <IconButton
              onClick={() => onClose?.()}
              size="small"
              sx={{ color: "text.secondary" }}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => onClose?.()} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || disableSubmit}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormDialog;

